import MomentUtils from '@date-io/moment';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import {
  Alert,
  Grid,
  Pagination,
  Table, TableBody, TableCell, TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import isEmpty from 'lodash.isempty';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageWrapper from '../../../common/PageWrapper/PageWrapper';
import { AuthorisationContext } from '../../../components/AuthorisationContext';
import { DashboardContext } from '../../../components/DashboardContext';
import { HorizontalScrollWrapper } from '../../../components/HorizontalScrollWrapper';
import './style.scss';

const Balance = () => {
  const { userRole } = useContext(AuthorisationContext);
  const {
    balanceFilials, fetchBalanceFilials, fetchBalanceSnapshots,
    fetchBalanceDepartments, fetchBalanceSnapshotDepartments,
    balanceDepartments, balanceDepartmentsCount,
    selectedFilialID: filialID,
    updateSelectedFilialID: setFilialID
  } = useContext(DashboardContext);
  const [page, setPage] = useState(Number(localStorage.getItem('adBalancePage')) || 1);
  const [perPage, setPerPage] = useState(Number(localStorage.getItem('adBalancePerPage')) || 5);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [snapshotError, setSnapshotError] = useState('');
  const [isViewingSnapshot, setIsViewingSnapshot] = useState(false);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    localStorage.setItem('adBalancePage', newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPerPage(parseInt(event.target.value, 10));
    localStorage.setItem('adBalancePerPage', String(parseInt(event.target.value, 10)));
    setPage(1);
  };

  const onFilialClick = (id) => {
    if (id) {
      setFilialID(id);
      setPage(1);
      localStorage.setItem('adBalancePage', 1);
    }
  };

  const paginationPerPage = useMemo(() => {
    const arr = [5, 10, 25, 50];
    return arr;
  }, []);

  useEffect(() => {
    if (filialID && userRole === 'Администратор') {
      const isToday = moment(selectedDate).isSame(moment(), 'day');
      if (!isToday) {
        fetchBalanceSnapshotDepartments({
          id: filialID,
          page,
          per_page: perPage,
          date: moment(selectedDate).format('YYYY-MM-DD')
        });
      } else {
        fetchBalanceDepartments({ id: filialID, page, per_page: perPage });
      }
    } else if (filialID) {
      fetchBalanceDepartments({ id: filialID, page, per_page: perPage });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filialID, page, perPage, selectedDate, userRole]);

  useEffect(() => {
    const fetchData = async () => {
      setSnapshotError('');
      setPage(1);
      localStorage.setItem('adBalancePage', 1);
      if (userRole === 'Администратор') {
        const isToday = moment(selectedDate).isSame(moment(), 'day');
        setIsViewingSnapshot(!isToday);
        if (isToday) {
          fetchBalanceFilials({});
        } else {
          try {
            const response = await fetchBalanceSnapshots({ date: moment(selectedDate).format('YYYY-MM-DD') });
            if (response && response.message) {
              setSnapshotError(response.message);
            }
          } catch (error) {
            setSnapshotError('Помилка при завантаженні знімку балансу');
          }
        }
      } else {
        setIsViewingSnapshot(false);
        fetchBalanceFilials({});
      }
    };
    fetchData();
  }, [userRole, selectedDate]);

  const currencies = useMemo(
    () => balanceFilials.at(-1)?.currencies?.map((currency, index) => {
      if (index === 0) {
        return 'Загальний баланс у гривні';
      }
      if (currency.currency_id === 'bank_average_usd') {
        return 'Minfin USD';
      }
      return currency.currency_name;
    }),
    [balanceFilials]);

  const renderCurrencies = useMemo(
    () => currencies?.map((currency) => <TableCell align="center" key={currencies.indexOf(currency)}>{currency}</TableCell>),
    [currencies]);

  const renderCityCurrency = useCallback(
    (item) => balanceFilials.at(-1)?.currencies?.map((currency) => {
      const currentCurrency = item.find((itemCurrency) => itemCurrency.currency_id === currency.currency_id) || {
        value: '-'
      };
      return (
        <TableCell key={currency.currency_id} align="right">
          {currentCurrency.value}
        </TableCell>
      );
    }), [balanceFilials]);

  const renderDepartmentCurrency = useCallback(
    (item) => balanceFilials.at(-1)?.currencies?.map((currency) => {
      const currentCurrency = item.find((itemCurrency) => itemCurrency.currency_id === currency.currency_id) || {
        balance: '-'
      };
      return (
        <TableCell key={currency.currency_id} align="right">
          {currentCurrency.balance}
        </TableCell>
      );
    }), [balanceFilials]);

  const renderFilials = useMemo(
    () => balanceFilials?.map((filial, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <TableRow key={`${filial.filial_name}-${index}`}>
        <TableCell
          onClick={() => onFilialClick(filial.filial_id)}
          sx={[
            filial.filial_id
            && filialID === filial.filial_id && {
              backgroundColor: 'var(--balance-button)!important'
            }
          ]}
        >
          {filial.filial_name || 'Загальний баланс'}
        </TableCell>
        {renderCityCurrency(filial.currencies)}
      </TableRow>
    )),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [balanceFilials, filialID]);

  const renderDepartments = useMemo(
    () => filialID
      && balanceDepartments?.map((department) => (
        <TableRow key={department.id}>
          {userRole === 'Администратор' && (
            <TableCell className="balance-edit">
              {!isViewingSnapshot ? (
                <Link to={`/balance/departments/${department.id}`} className="balance-edit-btn">
                  <DriveFileRenameOutlineIcon />
                </Link>
              ) : (
                <div className="balance-edit-btn" style={{ opacity: 0.3, cursor: 'not-allowed' }}>
                  <DriveFileRenameOutlineIcon />
                </div>
              )}
            </TableCell>
          )}
          {/* <TableCell align="center">{department.id}</TableCell> */}
          <TableCell sx={{ maxWidth: '200px' }}>{department.title}</TableCell>
          {renderDepartmentCurrency(department.balances)}
        </TableRow>
      )),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [balanceDepartments]);

  return (
    <PageWrapper>
      {userRole === 'Администратор' && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <div className="operation-list-selector">
              <LocalizationProvider dateAdapter={MomentUtils} locale="uk">
                <DatePicker
                  label="Дата"
                  mask="__.__.____"
                  value={selectedDate}
                  maxDate={moment()}
                  onChange={(newValue) => setSelectedDate(moment(newValue))}
                  renderInput={(params) => <TextField mask="YYYY-MM-DD" {...params} />}
                />
              </LocalizationProvider>
            </div>
          </Grid>
          {snapshotError && (
            <Grid item xs={12}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {snapshotError}
              </Alert>
            </Grid>
          )}
        </Grid>
      )}
      <HorizontalScrollWrapper className="balance-filials balanceCashier-container balance-cashier-table">
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow sx={{ verticalAlign: 'bottom' }}>
              <TableCell align="left" sx={{ maxWidth: '200px' }}>Регіон</TableCell>
              {renderCurrencies}
            </TableRow>
          </TableHead>
          <TableBody>{renderFilials}</TableBody>
        </Table>
      </HorizontalScrollWrapper>
      <HorizontalScrollWrapper className="balance-departments">
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="simple table" className="balance-cashier-table">
            <TableHead>
              <TableRow>
                {userRole === 'Администратор' && (<TableCell padding="checkbox" align="center">Дія</TableCell>)}
                {/* <TableCell padding="checkbox" align="center">ID</TableCell> */}
                <TableCell align="center" sx={{ maxWidth: '200px' }}>
                  Відділення
                </TableCell>
                {renderCurrencies}
              </TableRow>
            </TableHead>
            <TableBody>{renderDepartments}</TableBody>
          </Table>
          {(!filialID || isEmpty(balanceDepartments)) && (
            <div className="operations-emptyOperations">Оберіть місто для відображення балансів відділень</div>
          )}
          {balanceDepartmentsCount >= 5 && !isEmpty(balanceDepartments) && (
            <div className="users-pagination">
              <Pagination
                page={page}
                shape="rounded"
                onChange={handleChangePage}
                count={Math.ceil(balanceDepartmentsCount / perPage)}
                color="primary"
              />
              <TablePagination
                count={balanceDepartmentsCount || 1}
                page={page}
                component="div"
                rowsPerPage={perPage}
                labelRowsPerPage="Відображати:"
                onPageChange={handleChangePage}
                rowsPerPageOptions={paginationPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
          )}
        </TableContainer>
      </HorizontalScrollWrapper>
    </PageWrapper>
  );
};

export default Balance;
